import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  COACH_DEFAULT_WEEKS,
  COACH_REASONS,
  COACH_WEEK_CHOICES,
  CoachAdviceSchema,
  coachHasData,
  PHOTO_ESTIMATE_PROVIDERS,
  wallTimeInZone,
} from '@kraftwerk/shared';
import type {
  CoachAdvice,
  CoachReason,
  CoachSummary,
  CoachWeeks,
  PhotoEstimateProvider,
} from '@kraftwerk/shared';
import { ApiError, apiFetch } from '../../api.js';
import { loadCoachSummary } from '../../db/coach.js';
import { saveNote } from '../../db/notes.js';
import { today as todayIn } from '../../db/nutrition.js';
import { readProfile } from '../../db/profile.js';
import { formatDay } from '../../format.js';
import { useIsChild } from '../../hooks/useModules.js';

type CoachProvider = PhotoEstimateProvider;
type Problem = CoachReason | 'offline' | 'unknown';

/**
 * Gibt es den Coach für dieses Konto — und wer antwortet? Fragt den Server.
 * Ohne Anbieter, offline oder für Kinder: `null`, und der Knopf fehlt. Ein
 * unbekannter Anbieter zählt als „nein“: ohne passenden Hinweis kein Senden.
 */
export function useCoachProvider(): CoachProvider | null {
  const child = useIsChild();
  const [provider, setProvider] = useState<CoachProvider | null>(null);
  useEffect(() => {
    if (child) return;
    let live = true;
    apiFetch<{ available: boolean; provider?: string }>('/coach')
      .then((body) => {
        const known = (PHOTO_ESTIMATE_PROVIDERS as readonly string[]).includes(body.provider ?? '');
        if (live)
          setProvider(body.available === true && known ? (body.provider as CoachProvider) : null);
      })
      .catch(() => {
        if (live) setProvider(null);
      });
    return () => {
      live = false;
    };
  }, [child]);
  return child ? null : provider;
}

/**
 * KI-Coach (27.10.2026): Einstieg an der Rückschau, eigenständig gebaut — der
 * Umbau der Navigation hängt ihn nur um.
 *
 * Drei Schritte, keiner überspringbar: Zeitraum wählen → **die ganze
 * Zusammenfassung sehen**, mit Hinweis, wohin sie geht → erst „Senden“
 * schickt sie. Die Antwort wird nur angezeigt, nie gespeichert — außer man
 * merkt sie sich ausdrücklich als Gedanken (dann verschlüsselt wie jeder).
 */
export function CoachEntry(): React.JSX.Element | null {
  const { t, i18n } = useTranslation();
  const provider = useCoachProvider();
  const headingId = useId();
  const noticeId = useId();
  const resultRef = useRef<HTMLHeadingElement>(null);
  const [open, setOpen] = useState(false);
  const [weeks, setWeeks] = useState<CoachWeeks>(COACH_DEFAULT_WEEKS);
  const [summary, setSummary] = useState<CoachSummary | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [advice, setAdvice] = useState<{ advice: CoachAdvice; model: string } | null>(null);
  const [saved, setSaved] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle');

  useEffect(() => {
    if (!open) return;
    let live = true;
    setSummary(undefined);
    void loadCoachSummary(weeks).then((value) => {
      if (live) setSummary(value);
    });
    return () => {
      live = false;
    };
  }, [open, weeks]);

  useEffect(() => {
    if (advice) resultRef.current?.focus();
  }, [advice]);

  if (!provider) return null;

  const close = () => {
    setOpen(false);
    setAdvice(null);
    setProblem(null);
    setSaved('idle');
  };

  const send = () => {
    if (!summary) return;
    setBusy(true);
    setProblem(null);
    const language = i18n.language.startsWith('en') ? 'en' : 'de';
    apiFetch<{ advice: unknown; model: string }>('/coach/review', {
      method: 'POST',
      body: JSON.stringify({ summary, language }),
    })
      .then((body) => {
        // Auch die Antwort des eigenen Servers wird geprüft.
        const parsed = CoachAdviceSchema.safeParse(body.advice);
        if (!parsed.success) {
          setProblem('invalid');
          return;
        }
        setAdvice({ advice: parsed.data, model: String(body.model ?? '') });
      })
      .catch((error: unknown) => {
        if (!(error instanceof ApiError)) setProblem('offline');
        else if ((COACH_REASONS as readonly string[]).includes(error.reason ?? ''))
          setProblem(error.reason as CoachReason);
        else setProblem('unknown');
      })
      .finally(() => setBusy(false));
  };

  const remember = () => {
    if (!advice) return;
    setSaved('saving');
    const { observations, questions, suggestion } = advice.advice;
    const body = [
      ...observations.map((line) => `- ${line}`),
      ...questions.map((line) => `- ${line}`),
      ...(suggestion ? [`- ${suggestion}`] : []),
      '',
      t('coach.result.label'),
    ].join('\n');
    void readProfile()
      .then((profile) =>
        saveNote(
          todayIn(profile.timezone),
          {
            title: t('coach.note.title'),
            body,
            mood: null,
            time: wallTimeInZone(new Date(), profile.timezone),
            event: null,
          },
          null,
        ),
      )
      .then(() => setSaved('saved'))
      .catch(() => setSaved('failed'));
  };

  if (!open) {
    return (
      <section aria-labelledby={headingId} className="kw-tile flex flex-col gap-2">
        <h3 id={headingId} className="kw-kicker">
          {t('coach.heading')}
        </h3>
        <p className="text-sm text-ink-muted">{t('coach.intro')}</p>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="kw-btn kw-btn-quiet kw-btn-small"
        >
          {t('coach.open')}
        </button>
      </section>
    );
  }

  return (
    <section aria-labelledby={headingId} className="kw-tile flex flex-col gap-3">
      <h3 id={headingId} className="kw-kicker">
        {t('coach.heading')}
      </h3>

      {advice ? (
        <CoachResult
          advice={advice.advice}
          model={advice.model}
          headingRef={resultRef}
          saved={saved}
          onRemember={remember}
          onClose={close}
        />
      ) : (
        <>
          <fieldset className="flex flex-col gap-1">
            <legend className="kw-label">{t('coach.weeks')}</legend>
            <div className="flex flex-wrap gap-2">
              {COACH_WEEK_CHOICES.map((choice) => (
                <button
                  key={choice}
                  type="button"
                  aria-pressed={weeks === choice}
                  disabled={busy}
                  onClick={() => setWeeks(choice)}
                  className="kw-toggle"
                >
                  {t('coach.weekOption', { count: choice })}
                </button>
              ))}
            </div>
          </fieldset>

          {summary === undefined ? (
            <p className="text-sm text-ink-muted">{t('session.loading')}</p>
          ) : summary === null || !coachHasData(summary) ? (
            <>
              <p className="text-sm">{t('coach.noData')}</p>
              <button type="button" onClick={close} className="kw-btn kw-btn-quiet kw-btn-small">
                {t('coach.close')}
              </button>
            </>
          ) : (
            <>
              <div
                role="group"
                aria-labelledby={noticeId}
                className="flex flex-col gap-2 rounded-xl border border-warn p-3 text-sm"
              >
                <h4 id={noticeId} className="font-semibold">
                  {t(`coach.notice.heading.${provider}`)}
                </h4>
                <p>{t(`coach.notice.body.${provider}`)}</p>
              </div>
              <CoachPreview summary={summary} />
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={send}
                  disabled={busy}
                  className="kw-btn kw-btn-small flex-1"
                >
                  {busy ? t('coach.busy') : t('coach.send')}
                </button>
                <button
                  type="button"
                  onClick={close}
                  disabled={busy}
                  className="kw-btn kw-btn-quiet kw-btn-small flex-1"
                >
                  {t('coach.cancel')}
                </button>
              </div>
              {busy ? (
                // Ollama auf dem eigenen Server braucht Minuten (Geräteabnahme
                // 28.10.2026) — sagen, dass das Warten so gehört.
                <p role="status" className="text-sm text-ink-muted">
                  {t('coach.wait')}
                </p>
              ) : null}
            </>
          )}
          {problem ? (
            <p role="alert" className="text-sm text-warn">
              {t(`coach.error.${problem}`)}
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}

/**
 * Die Zusammenfassung, wie sie hinausgeht — lesbar, und darunter genau das,
 * was gesendet wird. Was fehlt, steht als „keine Angabe“ da, nie als 0.
 */
export function CoachPreview({ summary }: { summary: CoachSummary }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const none = t('coach.preview.none');
  const number = (value: number | null, digits = 0) =>
    value === null ? none : value.toLocaleString(i18n.language, { maximumFractionDigits: digits });
  const weekdayName = (weekday: number) =>
    // 5. Januar 2026 ist ein Montag.
    new Date(Date.UTC(2026, 0, 4 + weekday)).toLocaleDateString(i18n.language, {
      weekday: 'short',
      timeZone: 'UTC',
    });
  const day = (iso: string) => formatDay(iso, 'date', i18n.language);
  const { training, nutrition, supplements, journal } = summary;

  const sections: { key: string; title: string; lines: string[] }[] = [];
  if (training) {
    sections.push({
      key: 'training',
      title: t('coach.preview.training'),
      lines: [
        t('coach.preview.sessions', {
          count: training.sessions,
          perWeek: training.sessionsPerWeek.join(' · '),
        }),
        training.planned === null
          ? t('coach.preview.noPlan')
          : t('coach.preview.plan', {
              planned: training.planned,
              done: training.done ?? 0,
              missed: training.missed ?? 0,
              cancelled: training.cancelled ?? 0,
            }),
        ...(training.missedByWeekday.length > 0
          ? [
              t('coach.preview.missedBy', {
                list: training.missedByWeekday
                  .map((entry) => `${weekdayName(entry.weekday)} ${entry.missed}`)
                  .join(', '),
              }),
            ]
          : []),
        training.trainingDays.length > 0
          ? t('coach.preview.trainingDays', {
              list: training.trainingDays
                .map((entry) => `${weekdayName(entry.weekday)} ${entry.time}`)
                .join(', '),
            })
          : t('coach.preview.noTrainingDays'),
        t('coach.preview.volume', {
          list: training.volumeKgPerWeek.map((value) => number(value)).join(' · '),
        }),
        training.exercises.length > 0
          ? t('coach.preview.exercises', {
              list: training.exercises
                .map((entry) => `${entry.name} (${entry.sessions})`)
                .join(', '),
            })
          : t('coach.preview.noExercises'),
        t('coach.preview.rating', { value: number(training.avgRating, 1) }),
      ],
    });
  }
  if (nutrition) {
    sections.push({
      key: 'nutrition',
      title: t('coach.preview.nutrition'),
      lines: [
        t('coach.preview.daysLogged', { logged: nutrition.daysLogged, days: nutrition.days }),
        t('coach.preview.avgFood', {
          kcal: number(nutrition.avgKcal),
          protein: number(nutrition.avgProteinG),
        }),
        nutrition.kcalTarget === null
          ? t('coach.preview.noTargets')
          : t('coach.preview.targets', {
              kcal: number(nutrition.kcalTarget),
              protein: number(nutrition.proteinTargetG),
              days: number(nutrition.proteinGoalDays),
            }),
        t('coach.preview.water', {
          logged: nutrition.waterDaysLogged,
          avg: number(nutrition.avgWaterMl),
          target: number(nutrition.waterTargetMl),
          days: nutrition.waterGoalDays,
        }),
      ],
    });
  }
  sections.push({
    key: 'supplements',
    title: t('coach.preview.supplements'),
    lines: [
      supplements.due === 0
        ? t('coach.preview.noIntakes')
        : t('coach.preview.intakes', {
            due: supplements.due,
            taken: supplements.taken,
            skipped: supplements.skipped,
          }),
    ],
  });
  if (journal) {
    sections.push({
      key: 'journal',
      title: t('coach.preview.journal'),
      lines: [
        t('coach.preview.journalDays', { count: journal.daysWithEntry }),
        t('coach.preview.sleep', {
          hours: number(journal.avgSleepHours, 1),
          quality: number(journal.avgSleepQuality, 1),
        }),
        t('coach.preview.feeling', {
          energy: number(journal.avgEnergy, 1),
          mood: number(journal.avgMood, 1),
          soreness: number(journal.avgSoreness, 1),
        }),
      ],
    });
  }

  return (
    <div className="flex flex-col gap-2 text-sm">
      <p className="kw-label">
        {t('coach.preview.range', { from: day(summary.from), to: day(summary.to) })}
      </p>
      {sections.map((section) => (
        <div key={section.key} className="flex flex-col gap-1">
          <h4 className="font-semibold">{section.title}</h4>
          <ul className="flex list-disc flex-col gap-1 pl-5">
            {section.lines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      ))}
      <p className="text-xs text-ink-muted">{t('coach.preview.never')}</p>
      <details className="text-xs">
        <summary className="cursor-pointer">{t('coach.preview.raw')}</summary>
        <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-all">
          {JSON.stringify(summary, null, 2)}
        </pre>
      </details>
    </div>
  );
}

function CoachResult({
  advice,
  model,
  headingRef,
  saved,
  onRemember,
  onClose,
}: {
  advice: CoachAdvice;
  model: string;
  headingRef: React.RefObject<HTMLHeadingElement>;
  saved: 'idle' | 'saving' | 'saved' | 'failed';
  onRemember: () => void;
  onClose: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3 text-sm">
      <h4 ref={headingRef} tabIndex={-1} className="font-semibold">
        {t('coach.result.heading')}
      </h4>
      <p className="text-xs text-ink-muted">
        {t('coach.result.label')}
        {model ? ` · ${model}` : ''}
      </p>
      <ul className="flex list-disc flex-col gap-1 pl-5">
        {advice.observations.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      {advice.questions.length > 0 ? (
        <div className="flex flex-col gap-1">
          <h5 className="kw-label">{t('coach.result.questions')}</h5>
          <ul className="flex list-disc flex-col gap-1 pl-5">
            {advice.questions.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {advice.suggestion ? (
        <div className="flex flex-col gap-1">
          <h5 className="kw-label">{t('coach.result.suggestion')}</h5>
          <p>{advice.suggestion}</p>
        </div>
      ) : null}
      {advice.careHint ? (
        <p className="rounded-xl border border-line p-3">{t('coach.care')}</p>
      ) : null}
      {advice.removed > 0 ? (
        <p className="text-xs text-ink-muted">
          {t('coach.result.removed', { count: advice.removed })}
        </p>
      ) : null}
      <p className="text-xs text-ink-muted">{t('coach.result.notStored')}</p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onRemember}
          disabled={saved === 'saving' || saved === 'saved'}
          className="kw-btn kw-btn-quiet kw-btn-small flex-1"
        >
          {saved === 'saved' ? t('coach.note.saved') : t('coach.note.save')}
        </button>
        <button type="button" onClick={onClose} className="kw-btn kw-btn-quiet kw-btn-small flex-1">
          {t('coach.close')}
        </button>
      </div>
      {saved === 'failed' ? (
        <p role="alert" className="text-xs text-warn">
          {t('coach.note.failed')}
        </p>
      ) : null}
    </div>
  );
}
